import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import type { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import type {
	IncentiveRow,
	IncentiveReportSummary,
} from "../../models/IncentiveRow";

type Props = {
	rows: IncentiveRow[];
	summary: IncentiveReportSummary | undefined;
	isLoading: boolean;
	onGridReady: (api: GridApi) => void;
};

export const IncentiveReportGrid = ({
	rows,
	summary,
	isLoading,
	onGridReady,
}: Props) => {
	const { defaultColDef } = useGridDefinitions({});

	const columnDefs = useMemo(
		(): ColDef<IncentiveRow>[] => [
			{
				headerName: "Date",
				field: "ProductionDate",
				minWidth: 80,
				sort: "asc",
				valueFormatter: (params) => {
					if (!params.value) return "";
					const s = String(params.value).substring(0, 10); // "2026-05-01"
					const [y, m, d] = s.split("-");
					return `${m}/${d}/${y}`;
				},
			},
			{
				headerName: "Crew",
				field: "Crew",
				minWidth: 40,
				cellStyle: { textAlign: "center" },
			},
			{
				headerName: "Shift",
				field: "Shift",
				minWidth: 40,
				cellStyle: { textAlign: "center" },
			},
			{
				headerName: "Target",
				field: "DisplayedTarget",
				width: 80,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? params.value.toFixed(0) : "—",
			},
			{
				headerName: "Finished",
				field: "DisplayedFinished",
				minWidth: 80,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? params.value.toFixed(0) : "—",
			},
			{
				headerName: "Adj.",
				field: "Adjustment",
				minWidth: 40,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) => {
					const v = params.value;
					if (v === 0 || v == null) return "—";
					return v > 0 ? `+${v}` : String(v);
				},
			},
			{
				headerName: "Hrs",
				field: "HoursWorked",
				minWidth: 40,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) => (params.value ? String(params.value) : "—"),
			},
			{
				headerName: "Inc%",
				field: "IncPercent",
				minWidth: 40,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? `${params.value.toFixed(1)}%` : "—",
			},
			{
				headerName: "Override",
				field: "TargetTonsOverride",
				minWidth: 80,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? params.value.toFixed(0) : "—",
			},
			{
				headerName: "Override By",
				field: "LastOverrideBy",
				minWidth: 140,
				valueFormatter: (params) => params.value ?? "—",
			},
			{
				headerName: "Override At",
				field: "LastOverrideAt",
				minWidth: 140,
				valueFormatter: (params) => {
					if (!params.value) return "—";
					return dxtToLocalServerTime(params.value, "MM/dd/yyyy HH:mm") || "—";
				},
			},
			{
				headerName: "Notes",
				field: "Notes",
				flex: 1,
				minWidth: 600,
				cellStyle: {
					display: "flex",
					justifyContent: "flex-start",
					paddingLeft: "12px",
				},
			},
		],
		[],
	);

	return (
		<Paper
			elevation={2}
			sx={{
				height: "calc(100vh - 240px)",
				minHeight: "calc(100vh - 240px)",
				p: "10px",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<IncentiveRow>
					loading={isLoading}
					rowData={rows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					animateRows
					rowHeight={38}
					headerHeight={35}
					onGridReady={(params) => onGridReady(params.api)}
					loadingOverlayComponent={CenteredLazyLoading}
					domLayout="normal"
					suppressContextMenu
				/>
			</Box>
			{summary && (
				<Box display="flex" justifyContent="flex-end" gap={3} pt={1} pr={2}>
					<Typography variant="body2" color="text.secondary">
						<strong>Total Target:</strong>{" "}
						{summary.TotalTarget != null ? summary.TotalTarget.toFixed(0) : "—"}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						<strong>Total Finished:</strong>{" "}
						{summary.TotalFinished != null
							? summary.TotalFinished.toFixed(0)
							: "—"}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						<strong>Avg Inc%:</strong>{" "}
						{summary.AvgIncPercent != null
							? `${summary.AvgIncPercent.toFixed(1)}%`
							: "—"}
					</Typography>
				</Box>
			)}
		</Paper>
	);
};
